import type { Parent, ParentListParams, ParentSummary } from "../../services/studentService";

/**
 * The parents register pages on the server, so its filters become query
 * parameters (see ParentFilter in apps/students/views.py) and the view
 * switch's counts come from the summary endpoint.
 */

export const PARENT_PAGE_SIZES = [25, 50, 100];

export const RELATIONSHIPS: [string, string][] = [
  ["mother", "Mother"],
  ["father", "Father"],
  ["guardian", "Guardian"],
  ["grandmother", "Grandmother"],
  ["grandfather", "Grandfather"],
  ["aunt", "Aunt"],
  ["uncle", "Uncle"],
  ["other", "Other"],
];
export const relationshipLabel = (value?: string | null) => RELATIONSHIPS.find(([key]) => key === value)?.[1] ?? (value || "—");

export type ParentView = "all" | "unlinked" | "never" | "inactive" | "no_phone";

/** The view switch: its label, the query it adds, and the summary count it shows. */
export const PARENT_VIEWS: { key: ParentView; label: string; params: ParentListParams; count: keyof ParentSummary }[] = [
  { key: "all", label: "All", params: {}, count: "total" },
  { key: "unlinked", label: "No child linked", params: { linked: "false" }, count: "unlinked" },
  { key: "never", label: "Never signed in", params: { portal: "never" }, count: "never_signed_in" },
  { key: "inactive", label: "Login turned off", params: { portal: "inactive" }, count: "inactive" },
  { key: "no_phone", label: "No phone number", params: { no_phone: "true" }, count: "no_phone" },
];

// Every ordering ends on id so paging is stable when names repeat.
export const PARENT_ORDERINGS = {
  name: ["Name A–Z", "user__first_name,user__last_name,id"],
  surname: ["Surname A–Z", "user__last_name,user__first_name,id"],
  children: ["Most children", "-child_count,user__first_name,id"],
  recent: ["Signed in most recently", "-user__last_login,id"],
  newest: ["Added most recently", "-created_at,-id"],
} as const;

export interface ParentFilters {
  view: ParentView;
  search: string;
  relationship: string;
  childClass: string;
  ordering: keyof typeof PARENT_ORDERINGS;
}

export const EMPTY_PARENT_FILTERS: ParentFilters = { view: "all", search: "", relationship: "", childClass: "", ordering: "name" };

/** The list endpoint's query for these filters. */
export function parentParams(filters: ParentFilters): ParentListParams {
  const view = PARENT_VIEWS.find((item) => item.key === filters.view) ?? PARENT_VIEWS[0];
  const params: ParentListParams = { ...view.params, ordering: PARENT_ORDERINGS[filters.ordering][1] };
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.relationship) params.relationship = filters.relationship;
  if (filters.childClass) params.child_class = Number(filters.childClass);
  return params;
}

/** Narrowing filters in use; the view and the sort don't count. */
export const parentFilterCount = (filters: ParentFilters) => [filters.search.trim(), filters.relationship, filters.childClass].filter(Boolean).length;

/** Where the parent's portal login stands, for a status dot. */
export function portalState(parent: Pick<Parent, "portal_active" | "last_login" | "must_change_password">) {
  if (parent.portal_active === false) return { label: "Login off", dot: "bg-red-500" };
  if (!parent.last_login) return { label: "Never signed in", dot: "bg-amber-500" };
  if (parent.must_change_password) return { label: "Temporary password", dot: "bg-amber-500" };
  return { label: `Signed in ${parent.last_login.slice(0, 10)}`, dot: "bg-emerald-500" };
}

/** A parent's children as one line: "Joan (P3 A, primary), Peter (P1)". */
export function childrenLine(parent: Pick<Parent, "children_data">) {
  return (parent.children_data ?? []).map((child) => `${child.full_name} (${[child.class_name, child.is_primary ? "primary" : ""].filter(Boolean).join(", ") || "no class"})`).join("; ");
}

/** What stops a parent being deleted, or "" when nothing does. */
export function deleteBlocker(parent: Pick<Parent, "children_data">) {
  const count = parent.children_data?.length ?? 0;
  return count ? `Linked to ${count} ${count === 1 ? "child" : "children"}. Unlink them first, or turn the login off instead.` : "";
}
